import { hoursePowerMapping } from "constants/general";
import { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { searchListing, setPage } from "slices/searchListingReducer";
import { useAppSelector } from "store/appHooks";
import useQuery from "utils/router.util";
import ListingCards from "./ListingCards";
import SearchFilters from "../../components/search-filters/index";
import ExtraFilters from "components/extra-filters";
import {
  Box,
  ButtonBase,
  Grid,
  Pagination,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import ListFilters from "components/list-filters";
import MapComponent from "components/general/MapComponent";
import { useNavigate } from "react-router-dom";
import { ROUTES } from "constants/routes";

const SearchListing = () => {
  let query: any = useQuery();
  const dispatch: any = useDispatch();
  const navigate = useNavigate();
  const { searchListingReducer }: any = useAppSelector((state) => state);
  const [mapMarkers, setMapMarkers] = useState<Array<any>>([]);

  const theme = useTheme();
  const matchesSm = useMediaQuery(theme.breakpoints.down("sm"));
  const matchesXs = useMediaQuery(theme.breakpoints.only("xs"));

  useEffect(() => {
    let latLng = null;
    const qL = query.get("l");
    const qPage = query.get("page");
    const qReq = query.get("requirement");
    if (qL) {
      const split = qL?.split("~");
      latLng = {
        latitude: parseFloat(split[0]),
        longitude: parseFloat(split[1]),
      };
    }

    dispatch(
      searchListing({
        page: qPage ? Number(qPage ?? 0) : searchListingReducer.page,
        ...(latLng ? latLng : {}),
        ...(searchListingReducer.selectedHp > 0
          ? hoursePowerMapping[searchListingReducer.selectedHp]
          : {}),
        price_from: searchListingReducer.prices[0],
        price_to: searchListingReducer.prices[1],
        manufacturers: searchListingReducer.searchManufacturerIds,
        attachments: searchListingReducer.searchAttachmentIds,
        ...(qReq ? { listing_type: Number(qReq) } : { listing_type: 0 }),
        ...searchListingReducer.filters,
        instant_booking: searchListingReducer.instantBooking,
      })
    );
  }, [
    dispatch,
    query,
    searchListingReducer.page,
    searchListingReducer.selectedHp,
    searchListingReducer.prices,
    searchListingReducer.filters,
    searchListingReducer.searchAttachmentIds,
    searchListingReducer.searchManufacturerIds,
    searchListingReducer.instantBooking,
  ]);

  const { isMapView } = searchListingReducer;

  useEffect(() => {
    if (searchListingReducer.list.length > 0) {
      const marks: any = [];
      searchListingReducer.list.forEach((l: any) => {
        const listingAddr = {
          latitude: l.latitude,
          longitude: l.longitude,
          country: l.country,
          address_line_1: l.address_line_1,
          address_line_2: l.address_line_2,
          city: l.city,
          state: l.state,
          zipcode: l.zipcode,
        };
        if (listingAddr) {
          marks.push({
            lat: listingAddr?.latitude,
            lng: listingAddr?.longitude,
          });
        }
      });

      setMapMarkers(marks);
    }
  }, [searchListingReducer.list]);

  const redirectWithQuery = (property: string, value: string) => {
    var searchParams = new URLSearchParams(window.location.search);
    searchParams.set(property, value.toString());
    navigate(`${ROUTES.SEARCH_LISTING}?${searchParams}`);
  };

  return (
    <Box sx={{ pb: 6 }}>
      <SearchFilters />
      <ExtraFilters />
      <Grid container>
        <Grid
          item
          md={isMapView ? 8 : 12}
          sm={12}
          xs={12}
          lg={isMapView ? 8 : 12}
          xl={isMapView ? 8 : 12}
          sx={{ minHeight: "60vh" }}
        >
          <ListFilters />
          {searchListingReducer?.listMeta?.totalRecords > 0 && (
            <Typography
              sx={{
                mt: "10px",
                mb: {
                  md: "20px",
                  lg: "20px",
                  xl: "20px",
                  sm: "5px",
                  xs: "5px",
                },
                fontSize: {
                  md: "26px",
                  lg: "26px",
                  xl: "26px",
                  sm: "18px",
                  xs: "18px",
                },
                lineHeight: "31px",
                color: "#323232",
                fontWeight: 600,

                mx: "3.5%",
              }}
            >
              Over {searchListingReducer?.listMeta?.totalRecords}{" "}
              {searchListingReducer?.listMeta?.totalRecords === 1
                ? `Result`
                : `Results `}
              {isMapView ? "within map area" : ""}
            </Typography>
          )}
          {isMapView && (matchesSm || matchesXs) && (
            <Grid sm={12} xs={12} mb={3} sx={{ position: "relative" }}>
              <MapComponent
                marks={mapMarkers}
                height="30vh"
                onClick={() => {}}
                showTopGradiant={true}
              />
            </Grid>
          )}
          <ListingCards isMapView={isMapView} />
        </Grid>
        {isMapView && (
          <Grid md={4} lg={4} xl={4} sm={0} xs={0}>
            <MapComponent
              marks={mapMarkers}
              height="90vh"
              onClick={() => {}}
              showTopGradiant={false}
            />
          </Grid>
        )}
      </Grid>
      {searchListingReducer?.list?.length > 0 && (
        <Box
          sx={{
            display: "flex",
            px: "4%",
            py: "20px",
            justifyContent: "space-between",
            alignItems: "center",

            flexWrap: "no-wrap",
            gap: "20px",
          }}
        >
          <Box sx={{ flex: 1 }}>
            <Typography
              sx={{
                color: "#A2A2A2",
                fontSize: {
                  md: "13px",
                  lg: "13px",
                  xl: "13px",
                  sm: "10px",
                  xs: "10px",
                },
                fontWeight: 600,
              }}
            >
              Showing{" "}
              {searchListingReducer?.listMeta?.currentPage === 1
                ? 1
                : (searchListingReducer?.listMeta?.currentPage - 1) * 12}{" "}
              -{" "}
              {searchListingReducer?.listMeta?.currentPage * 12 >
              searchListingReducer?.listMeta?.totalRecords
                ? searchListingReducer?.listMeta?.totalRecords
                : searchListingReducer?.listMeta?.currentPage * 12}{" "}
              of {searchListingReducer?.listMeta?.totalRecords}
            </Typography>
          </Box>
          <Pagination
            count={searchListingReducer?.listMeta?.totalPages}
            shape="rounded"
            page={query.get("page") ? Number(query.get("page")) : 1}
            onChange={(_, page) => {
              dispatch(setPage(page));
              redirectWithQuery("page", page.toString());
            }}
            color="primary"
            autoFocus={false}
            size={matchesSm || matchesXs ? "small" : "medium"}
          />
        </Box>
      )}
    </Box>
  );
};

export default SearchListing;
